((root, factory) => {
  const inventory = typeof module === 'object' && module.exports
    ? require('./corkopia-dynamic-inventory.js')
    : root.CorkopiaDynamicInventory;
  const api = factory(root, inventory);
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.CorkopiaInventoryRefresh = api;
  if (root?.document) api.prepare(root.document, root);
})(typeof globalThis !== 'undefined' ? globalThis : window, (root, inventory) => {
  'use strict';

  const SURFACE_SELECTOR = '[data-dynamic-inventory-surface][data-inventory-section-id]';
  const CONTENT_SELECTOR = '[data-dynamic-inventory-content]';
  const registry = new Map();
  const pending = new Map();
  let flushTimer = null;
  let prepared = false;
  let observer = null;

  const text = (value) => value === null || value === undefined ? '' : String(value);
  const list = (value) => text(value).split(',').map((entry) => entry.trim()).filter(Boolean);
  const surfaceKey = (element) => text(
    element?.dataset?.inventorySurfaceId || element?.dataset?.inventorySectionId
  );
  const currentCards = (element) => {
    const wrappers = [...element.querySelectorAll('[data-home-product-card]')]
      .filter((card) => !card.closest('template'));
    return wrappers.length
      ? wrappers
      : [...element.querySelectorAll('[data-cork-product-card]')]
        .filter((card) => !card.closest('template'));
  };
  const cardIdentity = (card) => [
    card.dataset.productId || card.querySelector('[data-cork-product-card]')?.corkopiaProduct?.identity?.shopifyProductId,
    card.dataset.variantId || card.querySelector('[data-cork-product-card]')?.corkopiaProduct?.identity?.shopifyVariantId,
    card.dataset.pieceId || card.querySelector('[data-cork-product-card]')?.corkopiaProduct?.identity?.pieceId
  ].map(text).filter(Boolean);

  const readRequest = (element) => ({
    request_id: `surface-${surfaceKey(element)}`,
    surface: element.dataset.dynamicInventorySurface || 'home_rail',
    surface_id: surfaceKey(element),
    section_id: element.dataset.inventorySectionId || '',
    source_url: element.dataset.inventoryRefreshUrl || '',
    family: element.dataset.inventoryFamily || '',
    priorities: list(element.dataset.inventoryPriorities),
    limit: Number(element.dataset.inventoryLimit) || null,
    candidatePageSize: Number(element.dataset.inventoryCandidatePageSize) || null,
    balance: {
      enabled: element.dataset.inventoryBalanceEnabled === 'true',
      family_order: list(element.dataset.inventoryFamilyOrder),
      minimum_by_family: inventory.parseBoundMap(element.dataset.inventoryFamilyMinimums),
      maximum_by_family: inventory.parseBoundMap(element.dataset.inventoryFamilyMaximums)
    }
  });

  const healthForElement = (element, overrides = {}) => inventory.railHealth({
    eligible_count: Number(element.dataset.railEligibleCount) || currentCards(element).length,
    current_count: currentCards(element).filter((card) => !card.hidden).length,
    requested_count: Number(element.dataset.inventoryLimit) || 0,
    stale: element.dataset.inventoryNeedsRefresh === 'true',
    ...overrides
  });

  const applyHealth = (element, health, announce = false) => {
    element.dataset.railEligibleCount = String(health.eligible_count);
    element.dataset.railCurrentCount = String(health.current_count);
    element.dataset.railHealth = health.health;
    element.dataset.inventoryNeedsRefresh = String(health.needs_refresh);
    const output = element.querySelector('[data-rail-health]');
    if (output) {
      output.dataset.health = health.health;
      output.dataset.needsRefresh = String(health.needs_refresh);
      output.dataset.eligibleCount = String(health.eligible_count);
      output.dataset.currentCount = String(health.current_count);
    }
    const status = element.querySelector('[data-inventory-refresh-status]');
    if (announce && status) {
      status.textContent = health.health === 'DEPLETED'
        ? 'This group has no currently available pieces. Other current inventory is still available.'
        : `Current pieces refreshed. ${health.current_count} shown.`;
    }
    return health;
  };

  const findIncomingSurface = (parsedDocument, entry) =>
    [...parsedDocument.querySelectorAll(SURFACE_SELECTOR)].find((candidate) =>
      candidate.dataset.inventorySectionId === entry.request.section_id
      && candidate.dataset.dynamicInventorySurface === entry.request.surface
    ) || parsedDocument.querySelector(SURFACE_SELECTOR);

  const removeDuplicateCards = (content) => {
    const seen = new Set();
    const removed = [];
    currentCards(content).forEach((card) => {
      const identities = cardIdentity(card);
      const duplicate = identities.some((identity) => seen.has(identity));
      if (duplicate) {
        removed.push(...identities);
        card.remove();
        return;
      }
      identities.forEach((identity) => seen.add(identity));
    });
    return [...new Set(removed)];
  };

  const parseSectionHtml = (html, entry, windowRef = root) => {
    if (!html || !windowRef.DOMParser) return null;
    const parsed = new windowRef.DOMParser().parseFromString(html, 'text/html');
    const surface = findIncomingSurface(parsed, entry);
    const content = surface?.querySelector(CONTENT_SELECTOR);
    return surface && content ? { surface, content } : null;
  };

  const emit = (name, entry, health, reasonCode = '') => {
    const event = inventory.createEvent(name, {
      request_id: entry.request.request_id,
      surface: entry.request.surface,
      surface_id: entry.request.surface_id,
      family: entry.request.family,
      eligible_count: health.eligible_count,
      current_count: health.current_count,
      health: health.health,
      needs_refresh: health.needs_refresh,
      catalog_generation: null,
      reason_code: reasonCode
    });
    inventory.dispatchEvent(event, entry.element.ownerDocument);
    return event;
  };

  const reconcile = (entry, html, documentRef, windowRef) => {
    const incoming = parseSectionHtml(html, entry, windowRef);
    const currentContent = entry.element.querySelector(CONTENT_SELECTOR);
    if (!incoming || !currentContent) return {
      updated: false,
      code: 'SECTION_CONTENT_MISSING',
      health: applyHealth(entry.element, healthForElement(entry.element, { failed: true }))
    };

    const active = documentRef.activeElement?.closest?.('[data-home-product-card], [data-cork-product-card]');
    const focusIdentities = active ? cardIdentity(active) : [];
    currentContent.replaceChildren(...[...incoming.content.childNodes].map((node) =>
      documentRef.importNode(node, true)
    ));
    const duplicates = removeDuplicateCards(currentContent);
    entry.element.dataset.railEligibleCount = incoming.surface.dataset.railEligibleCount
      || String(currentCards(entry.element).length);
    entry.element.dataset.inventoryNeedsRefresh = 'false';

    root.CorkopiaProductRuntime?.normalizeWithin?.(currentContent);
    const EventConstructor = documentRef.defaultView?.CustomEvent || root.CustomEvent;
    documentRef.dispatchEvent(new EventConstructor('corkopia:inventory-rendered', {
      detail: {
        surface: entry.request.surface,
        surfaceId: entry.request.surface_id,
        sectionId: entry.request.section_id
      }
    }));

    if (focusIdentities.length) {
      const replacement = currentCards(entry.element).find((card) =>
        cardIdentity(card).some((identity) => focusIdentities.includes(identity))
      );
      replacement?.querySelector('button, a[href]')?.focus({ preventScroll: true });
    }

    const health = applyHealth(entry.element, healthForElement(entry.element), true);
    emit(inventory.EVENT_NAMES.railRefreshed, entry, health, duplicates.length ? 'DUPLICATES_REMOVED' : 'REFRESH_COMPLETE');
    if (health.health === 'DEPLETED') {
      entry.element.inventoryReplacementRequest = inventory.replacementRequest({
        reason: entry.request.surface === 'home_rail'
          ? 'REPLACE_EMPTY_RAIL'
          : 'REPLACE_SOLD_PIECE',
        surface: entry.request.surface,
        surface_id: entry.request.surface_id,
        family: entry.request.family
      });
      emit(inventory.EVENT_NAMES.depleted, entry, health, 'NO_CURRENT_ELIGIBLE_INVENTORY');
    }
    return { updated: true, code: 'REFRESH_COMPLETE', health, duplicates };
  };

  const requestUrl = (sourceUrl, sectionIds, windowRef) => {
    const url = new URL(sourceUrl || windowRef.location.href, windowRef.location.origin);
    url.searchParams.set('sections', sectionIds.join(','));
    return url;
  };

  const fetchGroup = async (entries, windowRef) => {
    const sectionIds = [...new Set(entries.map((entry) => entry.request.section_id).filter(Boolean))];
    if (!sectionIds.length) return entries.map((entry) => ({
      entry,
      error: new Error('Section ID is missing.')
    }));
    const url = requestUrl(entries[0].request.source_url, sectionIds, windowRef);
    try {
      const response = await windowRef.fetch(url.toString(), {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`Section refresh failed with ${response.status}.`);
      const payload = await response.json();
      return entries.map((entry) => ({
        entry,
        html: payload?.[entry.request.section_id] || ''
      }));
    } catch (error) {
      return entries.map((entry) => ({ entry, error }));
    }
  };

  const refreshEntries = async (entries, documentRef, windowRef) => {
    const groups = new Map();
    entries.forEach((entry) => {
      const source = entry.request.source_url || windowRef.location.href;
      if (!groups.has(source)) groups.set(source, []);
      groups.get(source).push(entry);
      entry.element.setAttribute('aria-busy', 'true');
      applyHealth(entry.element, healthForElement(entry.element, { refreshing: true }));
    });
    const outcomes = [];
    for (const group of groups.values()) {
      for (let index = 0; index < group.length; index += 5) {
        const responses = await fetchGroup(group.slice(index, index + 5), windowRef);
        responses.forEach(({ entry, html, error }) => {
          entry.element.removeAttribute('aria-busy');
          if (error) {
            const health = applyHealth(entry.element, healthForElement(entry.element, {
              failed: true,
              stale: true
            }));
            outcomes.push({ surface_id: entry.request.surface_id, updated: false, code: 'REFRESH_FAILED', health });
            return;
          }
          outcomes.push({
            surface_id: entry.request.surface_id,
            ...reconcile(entry, html, documentRef, windowRef)
          });
        });
      }
    }
    return outcomes;
  };

  const flush = async (documentRef, windowRef) => {
    flushTimer = null;
    const entries = [...pending.values()];
    pending.clear();
    if (!entries.length) return [];
    return refreshEntries(entries, documentRef, windowRef);
  };

  const schedule = (entry, reason, documentRef, windowRef) => {
    if (!entry || entry.element.getAttribute('aria-busy') === 'true') return false;
    entry.element.dataset.inventoryRefreshReason = reason;
    entry.element.dataset.inventoryNeedsRefresh = 'true';
    pending.set(entry.request.surface_id, entry);
    if (!flushTimer) {
      flushTimer = windowRef.setTimeout(() => { flush(documentRef, windowRef); }, 80);
    }
    return true;
  };

  const register = (element, documentRef, windowRef) => {
    const request = inventory.normalizeQuery(readRequest(element));
    if (!request.surface_id || !request.section_id) return null;
    const entry = { element, request };
    registry.set(request.surface_id, entry);
    element.dataset.inventoryRefreshReady = 'true';
    applyHealth(element, healthForElement(element));
    if (observer && element.dataset.inventoryAutoRefresh !== 'false') observer.observe(element);
    return entry;
  };

  const refresh = (surfaceId = '', reason = 'manual', documentRef = root.document, windowRef = root) => {
    const entries = surfaceId
      ? [registry.get(surfaceId)].filter(Boolean)
      : [...registry.values()];
    entries.forEach((entry) => schedule(entry, reason, documentRef, windowRef));
    return entries.length;
  };

  const prepare = (documentRef, windowRef) => {
    if (prepared || !documentRef) return;
    prepared = true;
    if ('IntersectionObserver' in windowRef) {
      observer = new windowRef.IntersectionObserver((entries) => {
        entries.forEach((observed) => {
          if (!observed.isIntersecting) return;
          observer.unobserve(observed.target);
          const entry = registry.get(surfaceKey(observed.target));
          schedule(entry, 'lazy_visibility', documentRef, windowRef);
        });
      }, { rootMargin: '600px 0px' });
    }
    const discover = () => documentRef.querySelectorAll(SURFACE_SELECTOR).forEach((element) => {
      if (!registry.has(surfaceKey(element))) register(element, documentRef, windowRef);
    });
    documentRef.addEventListener('DOMContentLoaded', discover);
    documentRef.addEventListener('shopify:section:load', discover);
    documentRef.addEventListener('corkopia:catalog-change', (event) => {
      const affected = new Set(event.detail?.affected_surfaces || []);
      [...registry.values()]
        .filter((entry) => !affected.size || affected.has(entry.request.surface))
        .forEach((entry) => schedule(entry, 'catalog_change', documentRef, windowRef));
    });
    documentRef.addEventListener('corkopia:piece-unavailable', () => {
      if (windowRef.CorkopiaOrchestrator) return;
      [...registry.values()].forEach((entry) =>
        schedule(entry, 'piece_unavailable', documentRef, windowRef)
      );
    });
    documentRef.addEventListener('visibilitychange', () => {
      if (documentRef.visibilityState === 'visible') refresh('', 'visibility_return', documentRef, windowRef);
    });
    windowRef.addEventListener?.('online', () => refresh('', 'network_return', documentRef, windowRef));
    if (documentRef.readyState !== 'loading') discover();
  };

  return Object.freeze({
    CONTENT_SELECTOR,
    SURFACE_SELECTOR,
    applyHealth,
    currentCards,
    healthForElement,
    parseSectionHtml,
    prepare,
    readRequest,
    reconcile,
    refresh,
    refreshEntries,
    registry,
    removeDuplicateCards,
    requestUrl
  });
});
